/**
 * Planilha do "Envio em massa" do funil.
 *
 * Mesmo padrão do broadcastCsv.js: CSV/TXT lido via FileReader, sem lib nova.
 * Colunas: `id` (obrigatória), `acao` (opcional), `mensagem` (opcional).
 * Primeira linha pode ser cabeçalho; separador `;` ou `,` detectado na 1ª linha.
 *
 * A resolução (id → empresa, ação → código) fica em funções puras para poder
 * ser verificada sem navegador.
 */

const REPLACEMENT = '�';

const HEADER_ALIASES = {
  id: ['id', 'empresa', 'company', 'companyid'],
  acao: ['acao', 'ação', 'action', 'tipo'],
  mensagem: ['mensagem', 'msg', 'message', 'comentario', 'comentário'],
};

export const normalizeText = (value) =>
  String(value == null ? '' : value)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

const detectDelimiter = (firstLine) => {
  const counts = [
    { char: ';', total: (firstLine.match(/;/g) || []).length },
    { char: ',', total: (firstLine.match(/,/g) || []).length },
    { char: '\t', total: (firstLine.match(/\t/g) || []).length },
  ];
  counts.sort((a, b) => b.total - a.total);
  return counts[0].total > 0 ? counts[0].char : ';';
};

const stripBom = (text) => text.replace(/^﻿/, '');

const unquote = (cell) => (cell || '').trim().replace(/^"(.*)"$/, '$1').trim();

const decode = (buffer) => {
  const utf8 = new TextDecoder('utf-8').decode(buffer);
  if (utf8.indexOf(REPLACEMENT) === -1) return utf8;
  try {
    return new TextDecoder('windows-1252').decode(buffer);
  } catch (e) {
    return utf8;
  }
};

const headerIndexes = (cells) => {
  const found = {};
  cells.forEach((cell, index) => {
    const key = normalizeText(cell);
    Object.keys(HEADER_ALIASES).forEach((column) => {
      if (found[column] === undefined && HEADER_ALIASES[column].indexOf(key) !== -1) {
        found[column] = index;
      }
    });
  });
  return found;
};

/**
 * Texto do CSV → linhas `{ rowNumber, id, acao, mensagem }`.
 * Sem cabeçalho, assume a ordem id; acao; mensagem.
 */
export const parseMassShippingCsv = (text) => {
  const lines = stripBom(text || '').split(/\r\n|\r|\n/);
  const nonEmpty = lines
    .map((line, index) => ({ line, rowNumber: index + 1 }))
    .filter((entry) => entry.line.trim().length > 0);

  if (!nonEmpty.length) return [];

  const delimiter = detectDelimiter(nonEmpty[0].line);
  const split = (line) => line.split(delimiter).map(unquote);

  const firstCells = split(nonEmpty[0].line);
  const header = headerIndexes(firstCells);
  const hasHeader = header.id !== undefined;

  const columns = hasHeader
    ? { id: header.id, acao: header.acao, mensagem: header.mensagem }
    : { id: 0, acao: 1, mensagem: 2 };

  const pick = (cells, index) => (index === undefined ? '' : cells[index] || '');

  return nonEmpty.slice(hasHeader ? 1 : 0).map(({ line, rowNumber }) => {
    const cells = split(line);
    return {
      rowNumber,
      id: pick(cells, columns.id),
      acao: pick(cells, columns.acao),
      mensagem: pick(cells, columns.mensagem),
    };
  });
};

export const readMassShippingCsv = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
    reader.onload = () => {
      try {
        resolve(parseMassShippingCsv(decode(reader.result)));
      } catch (error) {
        reject(error);
      }
    };
    reader.readAsArrayBuffer(file);
  });

/**
 * Valor da coluna `acao` (código numérico ou label) → `{ id, name }` de listAction.
 * Retorna null se não casar. `listAction` tem a opção "Selecione" com id '' — ignorada.
 */
export const resolveAction = (value, listAction) => {
  const raw = String(value == null ? '' : value).trim();
  if (!raw) return null;

  const options = (listAction || []).filter((opt) => opt.id !== '' && opt.id !== null && opt.id !== undefined);

  if (/^\d+$/.test(raw)) {
    const code = Number(raw);
    const byCode = options.find((opt) => Number(opt.id) === code);
    if (byCode) return byCode;
  }

  const key = normalizeText(raw);
  return options.find((opt) => normalizeText(opt.name) === key) || null;
};

const onlyDigits = (value) => String(value == null ? '' : value).replace(/\D/g, '');

/**
 * Índice das empresas devolvidas por BROADCAST_RESOLVE_COMPANIES
 * (`{ companyId, name, cnpj, code, contextCode }`) por id, código e CNPJ (só
 * dígitos), para casar a coluna `id` da planilha seja ela id ou CNPJ.
 */
const indexCompanies = (companies) => {
  const index = {};
  (companies || []).forEach((company) => {
    const id = String(company.companyId == null ? company.id : company.companyId).trim();
    if (id) index[id.toLowerCase()] = company;
    if (company.code) index[String(company.code).trim().toLowerCase()] = company;
    const cnpj = onlyDigits(company.cnpj);
    if (cnpj) index['cnpj:' + cnpj] = company;
  });
  return index;
};

const findCompany = (index, value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const direct = index[raw.toLowerCase()];
  if (direct) return direct;
  const cnpj = onlyDigits(raw);
  return cnpj.length >= 11 ? index['cnpj:' + cnpj] || null : null;
};

/**
 * Linhas do CSV → `{ resolved: [...], unresolved: [...] }`.
 *
 * `companies`: empresas resolvidas pela API v2 para os ids/CNPJs da planilha.
 * resolved:   { companyId, companyName, cnpj, code, contextCode, action: {id,name}|null, msg, source: 'csv', rowNumber }
 * unresolved: { rowNumber, value, reason }
 *
 * Ação ausente na linha cai para `defaultAction` (pode ser null → fica sem ação,
 * e o modal bloqueia o envio até o usuário escolher uma ação padrão).
 */
export const resolveCsvRows = (rows, companies, listAction, defaultAction) => {
  const index = indexCompanies(companies);

  const resolved = [];
  const unresolved = [];

  (rows || []).forEach((row) => {
    const id = String(row.id || '').trim();
    if (!id) {
      unresolved.push({ rowNumber: row.rowNumber, value: '', reason: 'Linha sem id' });
      return;
    }

    const company = findCompany(index, id);
    if (!company) {
      unresolved.push({ rowNumber: row.rowNumber, value: id, reason: 'Empresa não encontrada' });
      return;
    }

    let action = defaultAction || null;
    if (String(row.acao || '').trim()) {
      action = resolveAction(row.acao, listAction);
      if (!action) {
        unresolved.push({ rowNumber: row.rowNumber, value: `${id} / ${row.acao}`, reason: 'Ação desconhecida' });
        return;
      }
    }

    resolved.push({
      companyId: company.companyId == null ? company.id : company.companyId,
      companyName: company.name,
      cnpj: company.cnpj || '',
      code: company.code || '',
      contextCode: company.contextCode || '',
      action,
      msg: String(row.mensagem || '').trim() || null,
      source: 'csv',
      rowNumber: row.rowNumber,
    });
  });

  return { resolved, unresolved };
};

/** Junta novas entradas à seleção atual, sem duplicar empresa (a nova sobrescreve). */
export const mergeSelection = (current, incoming) => {
  const byId = {};
  const order = [];
  [].concat(current || [], incoming || []).forEach((item) => {
    if (!byId[item.companyId]) order.push(item.companyId);
    byId[item.companyId] = item;
  });
  return order.map((id) => byId[id]);
};

/** Divide em lotes de `size` (limite do backend: 200 por request). */
export const chunk = (items, size) => {
  const out = [];
  for (let i = 0; i < (items || []).length; i += size) out.push(items.slice(i, i + size));
  return out;
};

export const MASS_SHIPPING_CHUNK_SIZE = 200;

export const MASS_SHIPPING_TEMPLATE_NAME = 'modelo-envio-em-massa.csv';
export const MASS_SHIPPING_TEMPLATE_CONTENT =
  // Só o identificador: ação e mensagem vêm do modal. Sem linha de exemplo, que esquecida viraria termo de busca.
  '﻿id\r\n';

export const downloadMassShippingTemplate = () => {
  const blob = new Blob([MASS_SHIPPING_TEMPLATE_CONTENT], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = MASS_SHIPPING_TEMPLATE_NAME;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Grade de empresas do modal (mesma tabela do passo 2 de Avisos e Campanhas).
 *
 * As linhas têm o formato de BROADCAST_RESOLVE_COMPANIES
 * (`{ companyId, name, cnpj, code, contextCode }`). Empresas que entraram pelo
 * autocomplete ou pela planilha não passaram pela API e ficam sem cnpj/banco;
 * quando uma busca as devolve, a linha resolvida substitui a incompleta.
 *
 * A identidade aqui é só `companyId`, como no restante do envio em massa
 * (o POST do funil não recebe banco).
 */
export const rowFromSelection = (item) => ({
  companyId: item.companyId,
  name: item.companyName,
  cnpj: item.cnpj || '',
  code: item.code || '',
  contextCode: item.contextCode || '',
});

export const selectionFromRow = (row) => ({
  companyId: row.companyId,
  companyName: row.name,
  cnpj: row.cnpj || '',
  code: row.code || '',
  contextCode: row.contextCode || '',
  action: null,
  msg: null,
  source: 'busca',
  rowNumber: null,
});

/** Junta linhas sem duplicar empresa; campo vazio da nova não apaga o da antiga. */
export const mergeRows = (current, incoming) => {
  const byId = {};
  const order = [];
  [].concat(current || [], incoming || []).forEach((row) => {
    const id = String(row.companyId);
    if (!byId[id]) {
      order.push(id);
      byId[id] = Object.assign({}, row);
      return;
    }
    Object.keys(row).forEach((key) => {
      if (row[key] !== '' && row[key] !== null && row[key] !== undefined) byId[id][key] = row[key];
    });
  });
  return order.map((id) => byId[id]);
};

/**
 * Seleção nova a partir das linhas marcadas na grade.
 * Só mexe nas empresas que estão na grade: marcou → entra como 'busca';
 * desmarcou → sai da seleção. Itens já selecionados (planilha, manual)
 * continuam como estão, com a própria ação e mensagem.
 */
export const applyGridSelection = (selection, rows, checkedRows) => {
  const inGrid = {};
  (rows || []).forEach((row) => { inGrid[String(row.companyId)] = true; });
  const checked = {};
  (checkedRows || []).forEach((row) => { checked[String(row.companyId)] = row; });

  const kept = (selection || []).filter((item) => !inGrid[String(item.companyId)] || checked[String(item.companyId)]);
  const known = {};
  kept.forEach((item) => { known[String(item.companyId)] = true; });

  const added = (checkedRows || [])
    .filter((row) => !known[String(row.companyId)])
    .map(selectionFromRow);

  return kept.concat(added);
};

/**
 * Tira do campo de busca o termo que trouxe a empresa: se ficasse, a próxima
 * busca a traria de volta. Casa por id, código, CNPJ (com ou sem pontuação)
 * ou nome, sem diferenciar maiúsculas.
 */
export const termsWithoutCompany = (terms, row) => {
  const digits = (value) => String(value || '').replace(/\D/g, '');
  const norm = (value) => String(value || '').trim().toLowerCase();
  const ids = [row.companyId, row.contextCode, row.code, row.name].map(norm).filter((value) => value.length > 0);
  const cnpj = digits(row.cnpj);

  return (terms || []).filter((term) => {
    const t = norm(term);
    if (ids.indexOf(t) !== -1) return false;
    if (cnpj && digits(term) === cnpj) return false;
    return true;
  });
};



// WEBPACK FOOTER //
// ./src/vue/pages/restricted/funnel/massShippingCsv.js